"use client";

import type { ReactNode } from "react";
import { FocusScope } from "react-aria";
import { useSearchParams } from "next/navigation";
import { HeaderMenuButton } from "./header-menu-button";
import { useId, useCallback, useRef, useEffect, Suspense } from "react";
import { useHeaderContext } from "./header-context";

export type HeaderDropdownProperties = {
  label: string;
  children: ReactNode;
};
export const HeaderDropdownInternal = ({
  label,
  children,
}: HeaderDropdownProperties) => {
  const clickOutsideReference = useRef<HTMLSpanElement>(null);
  const id = useId();
  const { openId, setOpenId } = useHeaderContext();
  const isOpen = id === openId;
  const setOpen = useCallback(() => {
    setOpenId?.(id);
  }, [setOpenId]);

  const clickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        clickOutsideReference.current &&
        !clickOutsideReference.current.contains(event.target as Node)
      ) {
        setOpenId?.(id);
      }
    },
    [setOpenId, id],
  );

  const escapeKeyHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenId?.(id);
      }
    },
    [setOpenId, id],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", escapeKeyHandler);
      document.addEventListener("click", clickOutside);
      return () => {
        document.removeEventListener("keydown", escapeKeyHandler);
        document.removeEventListener("click", clickOutside);
      };
    }
  }, [isOpen, clickOutside]);

  const searchParameters = useSearchParams();
  const isEditMode =
    searchParameters.has("mgnlPreview", "false") &&
    searchParameters.has("mgnlChannel");

  return (
    <span className="flex flex-col" ref={clickOutsideReference}>
      <FocusScope contain={isOpen && !isEditMode}>
        <HeaderMenuButton isOpen={isOpen} setOpen={setOpen}>
          {label}
        </HeaderMenuButton>
        <div
          className="grid grid-rows-[0fr] overflow-hidden text-black transition-all duration-300 ease-in-out peer-aria-expanded:grid-rows-[1fr] peer-aria-expanded:bg-off-white md:absolute md:inset-x-0 md:top-full md:shadow-sm"
          {...{ inert: isOpen ? undefined : true }}
        >
          <div className="overflow-hidden">{children}</div>
        </div>
      </FocusScope>
    </span>
  );
};
export const HeaderDropdown = (parameters: HeaderDropdownProperties) => (
  <Suspense fallback={null}>
    <HeaderDropdownInternal {...parameters} />
  </Suspense>
);

HeaderDropdown.displayName = "Header.Dropdown";
